import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { getEmptyShippingProvider } from '../../interfaces/ShippingProvider/ShippingProvider';
import {
  createShippingProvider,
  deleteShippingProvider,
  getShippingProvider,
  updateShippingProvider,
} from '../../api/ShippingProvider';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';
import NumberInput from '../../components/NumberInput/NumberInput';

export const ModifyShippingProviderPage = () => {
  const navigate = useNavigate();

  const { shippingProviderID: shippingProviderIDAsStr } = useParams<string>();

  const shippingProviderID = shippingProviderIDAsStr
    ? Number(shippingProviderIDAsStr)
    : undefined;
  const isEditing = Number.isFinite(shippingProviderID);

  const [shippingProvider, setShippingProvider] = useState(
    getEmptyShippingProvider(),
  );
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);

  const refreshShippingProvider = () => {
    setLoading(true);
    getShippingProvider(shippingProviderID!)
      .then((sp) => setShippingProvider(sp))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isEditing) {
      refreshShippingProvider();
    }
  }, [shippingProviderIDAsStr]);

  const handleSave = () => {
    if (isEditing) {
      setLoading(true);
      updateShippingProvider(shippingProvider)
        .then((updatedShippingProvider) => {
          toast.success('Kargo firması başarıyla güncellendi');
          setShippingProvider(updatedShippingProvider);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createShippingProvider(shippingProvider)
        .then((createdShippingProvider) => {
          toast.success('Kargo firması başarıyla oluşturuldu');
          navigate(`/shipping/providers/${createdShippingProvider.id}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deleteShippingProvider(shippingProvider.id)
      .then(() => {
        toast.success('Kargo firması başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/shipping/providers');
  const invalidPrice = shippingProvider.price < 0;

  const discountedPriceErrors: string[] = [];
  if (shippingProvider.discountedPrice < 0) {
    discountedPriceErrors.push('Minimum değer: 0');
  }
  if (shippingProvider.discountedPrice > shippingProvider.price) {
    discountedPriceErrors.push('İndirimli fiyat baz fiyattan fazla olamaz');
  }

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili kargo firmasını silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? 'Kargo Firmasını Düzenle' : 'Yeni Kargo Firması'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 400,
          height: 'fit-content',
          p: 3,
          gap: 1.5,
        }}
      >
        <TextField
          label='İsim'
          variant='outlined'
          size='small'
          value={shippingProvider.name}
          onChange={(e) =>
            setShippingProvider({ ...shippingProvider, name: e.target.value })
          }
          fullWidth
        />
        <NumberInput
          label='Fiyat'
          value={shippingProvider.price}
          onChange={(newPrice) =>
            setShippingProvider({ ...shippingProvider, price: newPrice })
          }
          acceptFloat
          error={invalidPrice}
          helperText={invalidPrice ? 'Minimum değer: 0' : undefined}
          endAdornment={<Typography>₺</Typography>}
        />
        <NumberInput
          label='İndirimli Fiyat'
          value={shippingProvider.discountedPrice}
          onChange={(newDiscountedPrice) =>
            setShippingProvider({
              ...shippingProvider,
              discountedPrice: newDiscountedPrice,
            })
          }
          acceptFloat
          error={discountedPriceErrors.length > 0}
          helperText={
            discountedPriceErrors.length > 0
              ? discountedPriceErrors.join(', ')
              : undefined
          }
          endAdornment={<Typography>₺</Typography>}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={shippingProvider.isActive}
                onChange={() =>
                  setShippingProvider({
                    ...shippingProvider,
                    isActive: !shippingProvider.isActive,
                  })
                }
              />
            }
            label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant='contained'
            disabled={invalidPrice || discountedPriceErrors.length > 0}
            onClick={handleSave}
            sx={{ ...sharedStyles.buttonText }}
          >
            Kaydet
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
